export const DEFAULT_TIMESHEET_REPORTING_API = 'https://maxpark-firebase-sync-worker.onrender.com';

import {
  getReportingProxyHeaders,
  getReportingProxyUrl,
} from '../../../../utils/reportingClient';

const cleanUrl = value => String(value || '').trim().replace(/\/+$/, '');

const getReportingApiOrigin = configuredOrigin => cleanUrl(configuredOrigin);

const normalizeCount = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const toLocalDateInputValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 10);
};

export const getAgentTimesheetReportUrl = (options = {}) => {
  const origin = getReportingApiOrigin(options.reportingApi);
  const params = new URLSearchParams();

  if (options.date) {
    params.set('date', options.date);
  }

  if (options.eventLimit) {
    params.set('eventLimit', String(options.eventLimit));
  }

  if (!origin) return getReportingProxyUrl('agent-timesheets', params);
  return `${origin}/reports/agent-timesheets${params.toString() ? `?${params.toString()}` : ''}`;
};

const normalizeTimesheetAgent = (agent = {}, index = 0) => ({
  id: agent.agentKey || agent.agentId || agent.agentEmail || `agent-${index}`,
  agentKey: agent.agentKey || '',
  agentId: agent.agentId || '',
  agentEmail: agent.agentEmail || '',
  agentName: agent.agentName || agent.agentEmail || agent.agentId || 'Agent',
  firstSeenAt: agent.firstSeenAt || '',
  firstSeenLabel: agent.firstSeenLabel || '',
  lastSeenAt: agent.lastSeenAt || '',
  lastSeenLabel: agent.lastSeenLabel || '',
  estimatedMinutes: normalizeCount(agent.estimatedMinutes),
  eventCount: normalizeCount(agent.eventCount),
  appOpenCount: normalizeCount(agent.appOpenCount),
  ticketCreatedCount: normalizeCount(agent.ticketCreatedCount),
  ticketPrintedCount: normalizeCount(agent.ticketPrintedCount),
  searchCount: normalizeCount(agent.searchCount),
  latestEventType: agent.latestEventType || '',
  latestEventLabel: agent.latestEventLabel || '',
  latestLatitude: normalizeNumber(agent.latestLatitude),
  latestLongitude: normalizeNumber(agent.latestLongitude),
  latestAccuracyMeters: normalizeNumber(agent.latestAccuracyMeters),
});

const normalizeTimesheetEvent = (event = {}, index = 0) => ({
  id: event.eventId || `event-${index}`,
  agentKey: event.agentKey || '',
  agentId: event.agentId || '',
  agentEmail: event.agentEmail || '',
  agentName: event.agentName || event.agentEmail || event.agentId || 'Agent',
  eventType: event.eventType || '',
  eventLabel: event.eventLabel || event.eventType || '',
  ticketId: event.ticketId || '',
  occurredAt: event.occurredAt || '',
  timeLabel: event.timeLabel || '',
  latitude: normalizeNumber(event.latitude),
  longitude: normalizeNumber(event.longitude),
  accuracyMeters: normalizeNumber(event.accuracyMeters),
});

const normalizeTimesheetReport = (report = {}, fallbackDate = '') => {
  const agents = Array.isArray(report.agents)
    ? report.agents.map(normalizeTimesheetAgent)
    : [];
  const events = Array.isArray(report.events)
    ? report.events.map(normalizeTimesheetEvent)
    : [];

  return {
    date: report.date || fallbackDate,
    agents,
    events,
    totals: {
      agents: normalizeCount(report.totals?.agents || agents.length),
      events: normalizeCount(report.totals?.events || events.length),
      appOpens: normalizeCount(report.totals?.appOpens),
      ticketsCreated: normalizeCount(report.totals?.ticketsCreated),
      ticketsPrinted: normalizeCount(report.totals?.ticketsPrinted),
      searches: normalizeCount(report.totals?.searches),
    },
    loadedSources: Array.isArray(report.loadedSources) && report.loadedSources.length
      ? report.loadedSources
      : ['render-postgres'],
    dataSource: report.dataSource || 'render-postgres',
    loadedAt: report.loadedAt || new Date().toISOString(),
  };
};

export const loadAgentTimesheetData = async (options = {}) => {
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);

  if (!fetchImpl) {
    throw new Error('Impossible de charger les feuilles de temps: fetch non disponible.');
  }

  const headers = options.reportingApi
    ? { Accept: 'application/json' }
    : await getReportingProxyHeaders();
  const response = await fetchImpl(getAgentTimesheetReportUrl(options), {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger les feuilles de temps (${response.status}).`);
  }

  return normalizeTimesheetReport(await response.json(), options.date || '');
};
